import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import type { DocumentDetail, DocumentListRow } from "../types/documents";

const DOCUMENTS_QUERY_KEY = "documents";

type ApiResponse = {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
};

type ApiEndpoint = {
  $get: (args?: { query?: Record<string, string> }) => Promise<ApiResponse>;
  $post: (args: { json: Record<string, unknown> }) => Promise<ApiResponse>;
  $put: (args: { json: Record<string, unknown> }) => Promise<ApiResponse>;
  $delete: (args?: Record<string, never>) => Promise<ApiResponse>;
};

type DocumentsRoute = ApiEndpoint & {
  [documentId: number]: ApiEndpoint & {
    [action: string]: ApiEndpoint;
    versions: ApiEndpoint;
  };
  "upload-url": ApiEndpoint;
};

type DynamicCustomerRoutes = Record<
  string,
  { facilities: Record<string, { documents: DocumentsRoute }> }
>;

const dynamicCustomerRoutes = workClient.v1
  .customers as unknown as DynamicCustomerRoutes;

export interface ListDocumentsParams {
  customerId: string;
  facilityId: string | number; // UUID string or numeric ID
  search?: string;
  status?: "Published" | "Unpublished";
  categoryIds?: number[];
  assetId?: number;
  limit?: number;
  offset?: number;
}

export interface CreateDocumentPayload {
  documentName: string;
  fileId?: number;
  status: "Published" | "Unpublished";
  categoryIds?: number[];
  assetIds?: number[];
}

export interface DocumentUploadResult {
  fileId: number;
  externalFileName: string;
  uploadUrl: string;
  expiresIn: number;
}

export interface CreatedDocumentResult {
  documentId: number;
  documentNumber: string;
  version: string;
}

async function requestDocumentUploadUrl({
  customerId,
  facilityId,
  file,
  assetIds,
}: {
  customerId: string;
  facilityId: string;
  file: File;
  assetIds: number[];
}): Promise<DocumentUploadResult> {
  const response = await dynamicCustomerRoutes[customerId].facilities[
    facilityId
  ].documents["upload-url"].$post({
    json: {
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
      assetIds,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to create upload URL: ${response.status}`);
  }
  return response.json() as Promise<DocumentUploadResult>;
}

async function createDocument(
  customerId: string,
  facilityId: string,
  payload: CreateDocumentPayload,
): Promise<CreatedDocumentResult> {
  const response = await dynamicCustomerRoutes[customerId].facilities[
    String(facilityId)
  ].documents.$post({
    json: {
      documentName: payload.documentName,
      fileId:
        typeof payload.fileId === "number"
          ? payload.fileId
          : parseInt(String(payload.fileId || 0), 10),
      status: payload.status,
      categoryIds: payload.categoryIds,
      assetIds: payload.assetIds,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to create document: ${response.status}`);
  }
  return response.json() as Promise<CreatedDocumentResult>;
}

/** Persist explicitly retained AI source material through the existing Document API. */
export async function retainProcedureSourceMaterial({
  customerId,
  facilityId,
  file,
  assetIds = [],
}: {
  customerId: string;
  facilityId: string;
  file: File;
  assetIds?: number[];
}): Promise<CreatedDocumentResult> {
  if (assetIds.length === 0) {
    throw new Error(
      "Select an asset before retaining source material in Documents.",
    );
  }
  const upload = await requestDocumentUploadUrl({
    customerId,
    facilityId,
    file,
    assetIds,
  });
  const uploadResponse = await fetch(upload.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Document source upload failed: ${uploadResponse.status}`);
  }

  return createDocument(customerId, facilityId, {
    documentName: file.name,
    fileId: upload.fileId,
    status: "Unpublished",
    assetIds: assetIds.length > 0 ? assetIds : undefined,
  });
}

export const useDocumentUploadUrl = () => {
  return useMutation({
    mutationFn: async ({
      customerId,
      facilityId,
      file,
      assetIds,
    }: {
      customerId: string;
      facilityId: string;
      file: File;
      assetIds: number[];
    }) => {
      return requestDocumentUploadUrl({
        customerId,
        facilityId,
        file,
        assetIds,
      });
    },
  });
};

export const useListDocuments = (params: ListDocumentsParams) => {
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, params],
    queryFn: async () => {
      // facilityId is a UUID string from the work scope
      const facilityId = String(params.facilityId);
      const queryParams: Record<string, string> = {
        limit: (params.limit || 50).toString(),
        offset: (params.offset || 0).toString(),
      };

      if (params.search) queryParams.search = params.search;
      if (params.status) queryParams.status = params.status;
      if (params.categoryIds && params.categoryIds.length > 0) {
        queryParams.categoryIds = params.categoryIds.map(String).join(",");
      }
      if (params.assetId) queryParams.assetId = params.assetId.toString();

      const response = await dynamicCustomerRoutes[
        params.customerId
      ].facilities[facilityId].documents.$get({
        query: queryParams,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status}`);
      }

      return response.json() as Promise<{
        rows: DocumentListRow[];
        total: number;
      }>;
    },
  });
};

export const useDocumentDetail = (
  documentId: number,
  customerId: string,
  facilityId: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, documentId],
    queryFn: async () => {
      const response = await dynamicCustomerRoutes[customerId].facilities[
        facilityId
      ].documents[documentId].$get({});

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status}`);
      }

      return response.json() as Promise<DocumentDetail>;
    },
    enabled: enabled !== false && !!documentId && !!customerId && !!facilityId,
  });
};

export const useDocumentDownloadUrl = () => {
  return useMutation({
    mutationFn: async ({
      customerId,
      facilityId,
      documentId,
    }: {
      customerId: string;
      facilityId: string;
      documentId: number;
    }) => {
      const response = await dynamicCustomerRoutes[customerId].facilities[
        facilityId
      ].documents[documentId]["download-url"].$get({});
      if (!response.ok)
        throw new Error(`Failed to create download URL: ${response.status}`);
      return response.json() as Promise<{ downloadUrl: string }>;
    },
  });
};

export const useCreateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      facilityId,
      payload,
    }: {
      customerId: string;
      facilityId: string;
      payload: CreateDocumentPayload;
    }) => {
      return createDocument(customerId, facilityId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      documentId,
      facilityId,
      payload,
    }: {
      customerId: string;
      documentId: number;
      facilityId: string;
      payload: Partial<CreateDocumentPayload>;
    }) => {
      const response = await dynamicCustomerRoutes[customerId].facilities[
        facilityId
      ].documents[documentId].$put({
        json: payload,
      });

      if (!response.ok) {
        throw new Error(`Failed to update document: ${response.status}`);
      }

      return response.json() as Promise<{ success: boolean }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      documentId,
      facilityId,
    }: {
      customerId: string;
      documentId: number;
      facilityId: string;
    }) => {
      const response = await dynamicCustomerRoutes[customerId].facilities[
        facilityId
      ].documents[documentId].$delete({});

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.status}`);
      }

      return response.json() as Promise<{ success: boolean }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
  });
};

export const useUploadDocumentVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      documentId,
      facilityId,
      fileId,
      notes,
    }: {
      customerId: string;
      documentId: number;
      facilityId: string;
      fileId: number;
      notes?: string;
    }) => {
      const response = await dynamicCustomerRoutes[customerId].facilities[
        facilityId
      ].documents[documentId].versions.$post({
        json: { fileId, notes },
      });

      if (!response.ok) {
        throw new Error(`Failed to upload version: ${response.status}`);
      }

      return response.json() as Promise<{ version: string }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
  });
};
